import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
  type KeyboardEvent,
  type ClipboardEvent,
  type MouseEvent as ReactMouseEvent,
  type CSSProperties,
} from 'react'
import {
  MoreVertical,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  Copy,
  Filter,
  Search,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { DateInput } from '@/components/forms/DateInput'
import { formatDateFull } from '@/lib/dateFormat'
import { autoFitExcelColumnWidth, clampExcelColumnWidth, computeStickyOffsets, readExcelColumnWidths, retainExcelColumnWidths } from './excelColumnWidths'
import { useVirtualizer } from '@tanstack/react-virtual'

export type ExcelColumnType = 'text' | 'number' | 'currency' | 'date' | 'select'
export type SortDirection = 'asc' | 'desc'

export interface SortItem {
  key: string
  direction: SortDirection
}

export interface FilterItem {
  values?: Set<string>
  range?: {
    min?: number | null
    max?: number | null
  }
}

export type FilterState = Record<string, FilterItem>

export interface ExcelColumn<T> {
  key: keyof T & string
  label: string
  type?: ExcelColumnType
  width?: number | string
  minWidth?: number | string
  placeholder?: string
  align?: 'left' | 'center' | 'right'
  readOnly?: boolean | ((row: T) => boolean)
  sortable?: boolean
  filterable?: boolean
  /** Label shown in the filter list when it differs from the row's raw value. */
  filterLabel?: (row: T) => string
  formatter?: (val: unknown, row: T, rowIndex: number) => string
  renderCell?: (val: unknown, row: T, rowIndex: number) => ReactNode
  parser?: (rawStr: string, prevVal: unknown) => unknown
  inputDisplay?: boolean
  multiline?: boolean
  suggestions?: (row: T) => string[]
  onInputChange?: (value: string, row: T, rowIndex: number) => void
  headerTooltip?: string
  cellNotice?: (row: T) => string | null
  cellError?: (row: T) => string | null
  titleFormatter?: (val: unknown, row: T, rowIndex: number) => string
  stickyLeft?: number
  stickyRight?: number
  hidden?: boolean
  required?: boolean
  options?: Array<{ label: string; value: string }>
}

export interface ExcelEditableTableProps<T extends Record<string, unknown>> {
  columns: ExcelColumn<T>[]
  data: T[]
  onChange: (newData: T[]) => void
  onCopy?: (e: ClipboardEvent<HTMLDivElement>) => void
  onPasteDenied?: () => void
  onRowContextMenu?: (event: ReactMouseEvent<HTMLTableCellElement>, row: T, rowIndex: number, canReorder: boolean) => void
  onRowActionMenu?: (event: ReactMouseEvent<HTMLElement>, row: T, rowIndex: number, canReorder: boolean) => void
  onRowReorder?: (fromRow: T, toRow: T) => void
  onRowQuickAction?: (action: 'move-up' | 'move-down' | 'delete' | 'toggle-dim', row: T, rowIndex: number, canReorder: boolean) => void
  getRowQuickActionDisabledReason?: (action: 'move-up' | 'move-down' | 'delete' | 'toggle-dim', row: T) => string | null
  onRowFocus?: (row: T) => void
  getRowClassName?: (row: T) => string
  emptyRowFactory: () => T
  regenerateId?: (row: T) => T
  rowKey?: (row: T, index: number) => string
  readOnly?: boolean
  onNewRow?: (row: T) => T
  className?: string
  addRowLabel?: string
  emptyMessage?: string
  showActions?: boolean
  showAddRowButton?: boolean
  headerExtra?: ReactNode
  storageKey?: string
  widthStorageKey?: string
  resizableColumns?: boolean
  summaryRow?: ReactNode | ((visibleRows: T[], stickyOffsets?: Record<string, { left?: number; right?: number }>, visibleColumns?: ExcelColumn<T>[]) => ReactNode)
  variant?: 'default' | 'sheet'
  hiddenColumnsStorageKey?: string
  defaultHiddenColumnKeys?: readonly string[]
  columnVisibilityPresets?: Array<{ label: string; keys: readonly string[] }>
  fill?: boolean
  rowHeight?: number
  density?: 'compact' | 'normal'
}

/**
 * Resolves whether a column is read-only, supporting both boolean and (row: T) => boolean predicate.
 */
export function resolveReadOnly<T>(col: ExcelColumn<T> | undefined, row?: T): boolean {
  if (!col) return false
  if (typeof col.readOnly === 'function') {
    return row ? col.readOnly(row) : true
  }
  return Boolean(col.readOnly)
}

/**
 * Checks if a column is unconditionally read-only across all rows (i.e. readOnly === true, not a row-predicate).
 */
export function isColumnAlwaysReadOnly<T>(col: ExcelColumn<T> | undefined): boolean {
  if (!col) return false
  return col.readOnly === true
}

/**
 * Robust number parsing supporting both US format (1,234.56) and VN format (1.234,56).
 */
export function parseNumberValue(val: unknown): number | null {
  if (val === null || val === undefined) return null
  if (typeof val === 'number') return Number.isNaN(val) ? null : val
  const trimmed = String(val).trim()
  if (trimmed === '') return null

  // Both commas and dots present: e.g. "1,234.56" vs "1.234,56"
  if (trimmed.includes(',') && trimmed.includes('.')) {
    if (trimmed.lastIndexOf(',') > trimmed.lastIndexOf('.')) {
      // European/VN format: 1.234,56 -> 1234.56
      const normalized = trimmed.replace(/\./g, '').replace(',', '.')
      const n = parseFloat(normalized)
      return Number.isNaN(n) ? null : n
    }
    // US format: 1,234.56 -> 1234.56
    const normalized = trimmed.replace(/,/g, '')
    const n = parseFloat(normalized)
    return Number.isNaN(n) ? null : n
  }

  // Only commas: e.g. "7,450,000" or "0,5"
  if (trimmed.includes(',')) {
    if ((trimmed.match(/,/g) || []).length > 1) {
      // Multiple commas -> thousands separator: 7,450,000
      const n = parseFloat(trimmed.replace(/,/g, ''))
      return Number.isNaN(n) ? null : n
    }
    // Single comma: check digits after comma
    const parts = trimmed.split(',')
    if (parts[1].length === 3 && parts[0].length >= 1 && parseInt(parts[0], 10) >= 1) {
      const n = parseFloat(trimmed.replace(/,/g, ''))
      return Number.isNaN(n) ? null : n
    }
    const normalized = trimmed.replace(',', '.')
    const n = parseFloat(normalized)
    return Number.isNaN(n) ? null : n
  }

  // Only dots: e.g. "7.450.000" vs "12.5"
  if (trimmed.includes('.')) {
    if ((trimmed.match(/\./g) || []).length > 1) {
      // Multiple dots -> thousands separator in VN: 7.450.000
      const n = parseFloat(trimmed.replace(/\./g, ''))
      return Number.isNaN(n) ? null : n
    }
    const n = parseFloat(trimmed)
    return Number.isNaN(n) ? null : n
  }

  const n = parseFloat(trimmed)
  return Number.isNaN(n) ? null : n
}

/**
 * Parse raw tab-delimited text from clipboard (Excel / Google Sheets).
 */
export function parseTsv(text: string): string[][] {
  if (!text) return []
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (ch === '"') quoted = false
      else cell += ch === '\r' && text[i + 1] === '\n' ? (i++, '\n') : ch === '\r' ? '\n' : ch
      continue
    }
    if (ch === '"' && cell === '') quoted = true
    else if (ch === '\t') { row.push(cell); cell = '' }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++
      row.push(cell); rows.push(row); row = []; cell = ''
    } else cell += ch
  }
  row.push(cell)
  if (row.length > 1 || row[0] !== '' || rows.length === 0) rows.push(row)
  if (rows.length > 1 && rows[rows.length - 1]?.length === 1 && rows[rows.length - 1]?.[0] === '') rows.pop()
  return rows
}

/** Escape values using the quoted TSV rules used by Excel clipboard data. */
export function serializeTsv(rows: string[][]): string {
  return rows.map((row) => row.map((value) => /[\t\r\n"]/.test(value)
    ? `"${value.replace(/"/g, '""')}"`
    : value).join('\t')).join('\r\n')
}

/** Repeat one clipboard value across the selected rectangle, as Excel does. */
export function expandScalarTsvToRange(tsv: string, a: CellPos, b: CellPos): string {
  const parsed = parseTsv(tsv)
  const { r0, r1, c0, c1 } = normalizeCellRange(a, b)
  if (parsed.length !== 1 || parsed[0]?.length !== 1 || (r0 === r1 && c0 === c1)) return tsv
  return serializeTsv(Array.from({ length: r1 - r0 + 1 }, () =>
    Array.from({ length: c1 - c0 + 1 }, () => parsed[0]![0]!),
  ))
}

/**
 * Apply pasted 2D TSV data into table data, automatically appending rows
 * and sequentially assigning columns from the starting target cell.
 * If visibleIndices is provided, rows are pasted into visible originalIndices,
 * leaving hidden/filtered rows intact.
 */
export function applyTsvToData<T extends Record<string, unknown>>({
  tsv,
  data,
  columns,
  emptyRowFactory,
  startRow = 0,
  startCol = 0,
  visibleIndices,
}: {
  tsv: string
  data: T[]
  columns: ExcelColumn<T>[]
  emptyRowFactory: () => T
  startRow?: number
  startCol?: number
  visibleIndices?: number[]
}): T[] {
  const parsedRows = parseTsv(tsv)
  if (parsedRows.length === 0) return data

  const next = [...data]

  // Detect whether paste starts from STT or first editable column:
  let actualStartCol = startCol
  const editableColsCount = columns.filter((c) => !isColumnAlwaysReadOnly(c)).length
  if (startCol === 0 && isColumnAlwaysReadOnly(columns[0]) && parsedRows[0].length === editableColsCount) {
    const firstEditableIdx = columns.findIndex((c) => !isColumnAlwaysReadOnly(c))
    if (firstEditableIdx !== -1) {
      actualStartCol = firstEditableIdx
    }
  }

  if (visibleIndices) {
    parsedRows.forEach((rowCells, rOff) => {
      const vIndex = startRow + rOff
      let targetRowObj: T
      let actualDataIndex: number

      if (vIndex < visibleIndices.length) {
        actualDataIndex = visibleIndices[vIndex]
        targetRowObj = { ...next[actualDataIndex] }
      } else {
        actualDataIndex = next.length
        targetRowObj = emptyRowFactory()
        next.push(targetRowObj)
      }

      rowCells.forEach((cellRaw, cOff) => {
        const colIndex = actualStartCol + cOff
        if (colIndex >= columns.length) return
        const col = columns[colIndex]
        if (resolveReadOnly(col, targetRowObj)) return

        const trimmed = cellRaw
        if (col.parser) {
          targetRowObj[col.key] = col.parser(trimmed, targetRowObj[col.key]) as T[keyof T & string]
        } else if (col.type === 'number' || (col.type as string) === 'currency') {
          const parsedNum = parseNumberValue(trimmed)
          targetRowObj[col.key] = (parsedNum !== null ? parsedNum : '') as T[keyof T & string]
        } else {
          targetRowObj[col.key] = trimmed as T[keyof T & string]
        }
      })

      next[actualDataIndex] = targetRowObj
    })

    return next
  }

  // Sequential mode without visibleIndices mapping
  const targetRowCount = startRow + parsedRows.length
  while (next.length < targetRowCount) {
    next.push(emptyRowFactory())
  }

  parsedRows.forEach((rowCells, rOff) => {
    const rIndex = startRow + rOff
    const rowObj = { ...next[rIndex] }

    rowCells.forEach((cellRaw, cOff) => {
      const colIndex = actualStartCol + cOff
      if (colIndex >= columns.length) return
      const col = columns[colIndex]
      if (resolveReadOnly(col, rowObj)) return

      const trimmed = cellRaw
      if (col.parser) {
        rowObj[col.key] = col.parser(trimmed, rowObj[col.key]) as T[keyof T & string]
      } else if (col.type === 'number' || (col.type as string) === 'currency') {
        const parsedNum = parseNumberValue(trimmed)
        rowObj[col.key] = (parsedNum !== null ? parsedNum : '') as T[keyof T & string]
      } else {
        rowObj[col.key] = trimmed as T[keyof T & string]
      }
    })

    next[rIndex] = rowObj
  })

  return next
}

/** Row manipulation helpers */
export function insertRow<T>(data: T[], index: number, newRow: T): T[] {
  const next = [...data]
  next.splice(index + 1, 0, newRow)
  return next
}

export function deleteRow<T>(data: T[], index: number): T[] {
  return data.filter((_, i) => i !== index)
}

export function moveRowUp<T>(data: T[], index: number): T[] {
  if (index <= 0 || index >= data.length) return data
  const next = [...data]
  const temp = next[index]
  next[index] = next[index - 1]
  next[index - 1] = temp
  return next
}

export function moveRowDown<T>(data: T[], index: number): T[] {
  if (index < 0 || index >= data.length - 1) return data
  const next = [...data]
  const temp = next[index]
  next[index] = next[index + 1]
  next[index + 1] = temp
  return next
}

export function duplicateRow<T>(
  data: T[],
  index: number,
  regenerateId?: (row: T) => T,
): T[] {
  if (index < 0 || index >= data.length) return data
  const next = [...data]
  const source = data[index]
  let cloned: T
  if (regenerateId) {
    cloned = regenerateId(source)
  } else if (
    source &&
    typeof source === 'object' &&
    'id' in source &&
    typeof (source as Record<string, unknown>).id === 'string'
  ) {
    const oldId = (source as Record<string, unknown>).id as string
    const newId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${oldId}-copy-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    cloned = { ...source, id: newId }
  } else {
    cloned = { ...source }
  }
  next.splice(index + 1, 0, cloned)
  return next
}

export type CellPos = { row: number; col: number }

/** Inclusive rectangular range between two cells (Excel-style). */
export function normalizeCellRange(
  a: CellPos,
  b: CellPos,
): { r0: number; r1: number; c0: number; c1: number } {
  return {
    r0: Math.min(a.row, b.row),
    r1: Math.max(a.row, b.row),
    c0: Math.min(a.col, b.col),
    c1: Math.max(a.col, b.col),
  }
}

export function isCellInRange(row: number, col: number, a: CellPos, b: CellPos): boolean {
  const { r0, r1, c0, c1 } = normalizeCellRange(a, b)
  return row >= r0 && row <= r1 && col >= c0 && col <= c1
}

export function emptyValueForColumn<T>(column: ExcelColumn<T>): unknown {
  if (column.type === 'number' || (column.type as string) === 'currency') return null
  return ''
}

/** Clear editable cells inside the range; skip readOnly columns. */
export function clearCellsInRange<T extends Record<string, unknown>>(
  data: T[],
  columns: ExcelColumn<T>[],
  a: CellPos,
  b: CellPos,
): T[] {
  const { r0, r1, c0, c1 } = normalizeCellRange(a, b)
  const next = data.map((row) => ({ ...row }))
  for (let r = r0; r <= r1; r++) {
    if (r < 0 || r >= next.length) continue
    const updated = { ...next[r] }
    for (let c = c0; c <= c1; c++) {
      const col = columns[c]
      if (!col || resolveReadOnly(col, updated)) continue
      updated[col.key] = emptyValueForColumn(col) as T[typeof col.key]
    }
    next[r] = updated
  }
  return next
}

/** Delete multiple rows by index (any order); returns new array. */
export function deleteRowsByIndices<T>(data: T[], indices: Iterable<number>): T[] {
  const remove = new Set(
    [...indices].filter((i) => Number.isInteger(i) && i >= 0 && i < data.length),
  )
  if (remove.size === 0) return data
  return data.filter((_, i) => !remove.has(i))
}

/** Multi-level comparator following Excel rules (blanks last, Vietnamese locale for text). */
export function compareRows<T extends Record<string, unknown>>(
  rowA: T,
  rowB: T,
  sortConfig: SortItem[],
  columns: ExcelColumn<T>[],
): number {
  for (const sortItem of sortConfig) {
    const col = columns.find((c) => c.key === sortItem.key)
    const valA = rowA[sortItem.key]
    const valB = rowB[sortItem.key]

    const isBlankA = valA === null || valA === undefined || String(valA).trim() === ''
    const isBlankB = valB === null || valB === undefined || String(valB).trim() === ''

    if (isBlankA && isBlankB) continue
    // Blanks always sort to the end, regardless of asc/desc (Excel convention)
    if (isBlankA) return 1
    if (isBlankB) return -1

    const isNumOrCur = col?.type === 'number' || (col?.type as string) === 'currency'
    let diff = 0

    if (isNumOrCur) {
      const numA = parseNumberValue(valA)
      const numB = parseNumberValue(valB)
      if (numA !== null && numB !== null) {
        diff = numA - numB
      } else {
        diff = String(valA).localeCompare(String(valB), 'vi', { numeric: true, sensitivity: 'base' })
      }
    } else {
      diff = String(valA).localeCompare(String(valB), 'vi', { numeric: true, sensitivity: 'base' })
    }

    if (diff !== 0) {
      return sortItem.direction === 'asc' ? diff : -diff
    }
  }
  return 0
}

/** Check if row passes all active column filters (AND conjunction). */
export function matchesFilters<T extends Record<string, unknown>>(
  row: T,
  filterState: FilterState,
  _columns?: ExcelColumn<T>[],
): boolean {
  for (const key of Object.keys(filterState)) {
    const filter = filterState[key]
    if (!filter) continue

    // Range filter (for number/currency)
    const hasMin = filter.range?.min != null && !Number.isNaN(filter.range.min)
    const hasMax = filter.range?.max != null && !Number.isNaN(filter.range.max)
    if (hasMin || hasMax) {
      const val = row[key]
      const num = parseNumberValue(val)
      if (num === null) return false
      if (hasMin && num < (filter.range!.min as number)) return false
      if (hasMax && num > (filter.range!.max as number)) return false
      continue
    }

    // Set filter (values checklist)
    if (filter.values) {
      const val = row[key]
      const str = val !== null && val !== undefined ? String(val) : ''
      if (!filter.values.has(str)) return false
    }
  }
  return true
}

/** Check if a specific column has an active filter applied. */
export function isColumnFilterActive<T extends Record<string, unknown>>(
  colKey: string,
  filterState: FilterState,
  allData: T[],
): boolean {
  const filter = filterState[colKey]
  if (!filter) return false

  const hasMin = filter.range?.min != null && !Number.isNaN(filter.range.min)
  const hasMax = filter.range?.max != null && !Number.isNaN(filter.range.max)
  if (hasMin || hasMax) return true

  if (filter.values) {
    for (const r of allData) {
      const v = r[colKey]
      const str = v !== null && v !== undefined ? String(v) : ''
      if (!filter.values.has(str)) return true
    }
  }

  return false
}

export type VisibleRow<T> = {
  originalIndex: number
  row: T
}

/**
 * Computes visible rows mapped to originalIndex, applying active filters and multi-column sort.
 */
export function computeVisibleRows<T extends Record<string, unknown>>(
  data: T[],
  filterState: FilterState,
  sortConfig: SortItem[],
  columns: ExcelColumn<T>[],
): Array<VisibleRow<T>> {
  let list: Array<VisibleRow<T>> = data.map((row, originalIndex) => ({ originalIndex, row }))

  // 1. Filter
  if (Object.keys(filterState).length > 0) {
    list = list.filter(({ row }) => matchesFilters(row, filterState, columns))
  }

  // 2. Sort
  if (sortConfig.length > 0) {
    list = [...list].sort((a, b) => compareRows(a.row, b.row, sortConfig, columns))
  }

  return list
}

/**
 * Commits a cell edit at a visible row index, mapping through originalIndex in the full data array.
 */
export function commitCellAtVisible<T extends Record<string, unknown>>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  visibleRowIndex: number,
  key: keyof T & string,
  val: unknown,
): T[] {
  const target = visibleRows[visibleRowIndex]
  if (!target) return data
  const origIdx = target.originalIndex
  const next = [...data]
  next[origIdx] = { ...next[origIdx], [key]: val }
  return next
}

/**
 * Deletes visible rows (single or multiple) by mapping their visible indices to original indices.
 */
export function deleteVisibleRows<T>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  selectedVisibleIndices: Iterable<number>,
): T[] {
  const origIndices = new Set<number>()
  for (const vIdx of selectedVisibleIndices) {
    if (visibleRows[vIdx]) {
      origIndices.add(visibleRows[vIdx].originalIndex)
    }
  }
  return deleteRowsByIndices(data, origIndices)
}

/**
 * Clears editable cells inside a visible cell range, preserving hidden rows.
 */
export function clearVisibleCellsInRange<T extends Record<string, unknown>>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  columns: ExcelColumn<T>[],
  a: CellPos,
  b: CellPos,
): T[] {
  const { r0, r1, c0, c1 } = normalizeCellRange(a, b)
  const next = data.map((r) => ({ ...r }))
  for (let v = r0; v <= r1; v++) {
    if (v < 0 || v >= visibleRows.length) continue
    const origIdx = visibleRows[v].originalIndex
    const rowObj = { ...next[origIdx] }
    for (let c = c0; c <= c1; c++) {
      const col = columns[c]
      if (!col || resolveReadOnly(col, rowObj)) continue
      rowObj[col.key] = emptyValueForColumn(col) as T[typeof col.key]
    }
    next[origIdx] = rowObj
  }
  return next
}

/**
 * Swaps two adjacent visible rows when move up/down is triggered.
 * Automatically no-ops when sort is active or when at visible boundaries.
 */
export function swapVisibleRows<T>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  visibleRowIndex: number,
  direction: 'up' | 'down',
  sortConfig: SortItem[] = [],
): T[] {
  if (sortConfig.length > 0) return data
  if (direction === 'up') {
    if (visibleRowIndex <= 0 || visibleRowIndex >= visibleRows.length) return data
    const curOrigIdx = visibleRows[visibleRowIndex].originalIndex
    const targetOrigIdx = visibleRows[visibleRowIndex - 1].originalIndex
    const next = [...data]
    const temp = next[curOrigIdx]
    next[curOrigIdx] = next[targetOrigIdx]
    next[targetOrigIdx] = temp
    return next
  } else {
    if (visibleRowIndex < 0 || visibleRowIndex >= visibleRows.length - 1) return data
    const curOrigIdx = visibleRows[visibleRowIndex].originalIndex
    const targetOrigIdx = visibleRows[visibleRowIndex + 1].originalIndex
    const next = [...data]
    const temp = next[curOrigIdx]
    next[curOrigIdx] = next[targetOrigIdx]
    next[targetOrigIdx] = temp
    return next
  }
}

/**
 * Inserts a new row immediately below the specified visible row.
 */
export function insertVisibleRow<T>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  visibleRowIndex: number,
  newRow: T,
): T[] {
  const target = visibleRows[visibleRowIndex]
  if (!target) return data
  return insertRow(data, target.originalIndex, newRow)
}

/**
 * Duplicates a visible row immediately below it, generating a new id if string id exists.
 */
export function duplicateVisibleRow<T>(
  data: T[],
  visibleRows: Array<VisibleRow<T>>,
  visibleRowIndex: number,
  regenerateId?: (row: T) => T,
): T[] {
  const target = visibleRows[visibleRowIndex]
  if (!target) return data
  return duplicateRow(data, target.originalIndex, regenerateId)
}

/**
 * Appends a new row to the end of data.
 */
export function addTableRow<T>(data: T[], newRow: T): T[] {
  return [...data, newRow]
}

function RowActionMenu({
  onInsertBelow,
  onDelete,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  canMoveUp,
  canMoveDown,
  isSorted,
  row,
  rowIndex,
  onOpenMenu,
}: {
  onInsertBelow: () => void
  onDelete: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDuplicate: () => void
  canMoveUp: boolean
  canMoveDown: boolean
  isSorted?: boolean
  row?: Record<string, unknown>
  rowIndex?: number
  onOpenMenu?: (event: ReactMouseEvent<HTMLElement>, row: Record<string, unknown>, rowIndex: number, canReorder: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const moveDisabledTooltip = isSorted ? 'Bỏ sắp xếp để di chuyển dòng thủ công' : undefined

  return (
    <div className="relative inline-flex items-center justify-center" ref={menuRef}>
      <button
        type="button"
        onClick={(event) => {
          if (onOpenMenu && row && rowIndex !== undefined) { onOpenMenu(event, row, rowIndex, !isSorted); return }
          setOpen((prev) => !prev)
        }}
        aria-label="Thao tác dòng"
        className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-1 w-44 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/10 border border-slate-200 focus:outline-none font-sans">
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onInsertBelow()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <Plus className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Thêm dòng bên dưới
          </button>

          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onDuplicate()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
          >
            <Copy className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Nhân đôi dòng
          </button>

          <button
            type="button"
            disabled={!canMoveUp}
            title={!canMoveUp ? moveDisabledTooltip : undefined}
            onClick={() => {
              if (!canMoveUp) return
              setOpen(false)
              onMoveUp()
            }}
            className={`flex w-full items-center px-3 py-1.5 text-xs transition-colors ${
              canMoveUp
                ? 'text-slate-700 hover:bg-slate-50 cursor-pointer'
                : 'text-slate-300 cursor-not-allowed'
            }`}
          >
            <ArrowUp className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Di chuyển lên
          </button>

          <button
            type="button"
            disabled={!canMoveDown}
            title={!canMoveDown ? moveDisabledTooltip : undefined}
            onClick={() => {
              if (!canMoveDown) return
              setOpen(false)
              onMoveDown()
            }}
            className={`flex w-full items-center px-3 py-1.5 text-xs transition-colors ${
              canMoveDown
                ? 'text-slate-700 hover:bg-slate-50 cursor-pointer'
                : 'text-slate-300 cursor-not-allowed'
            }`}
          >
            <ArrowDown className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Di chuyển xuống
          </button>

          <div className="my-1 border-t border-slate-100" />

          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onDelete()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5 text-red-500" />
            Xóa dòng
          </button>
        </div>
      )}
    </div>
  )
}

function EditableCell<T extends Record<string, unknown>>({
  row,
  rowIndex,
  column,
  colIndex,
  onCommit,
  onCellFocus,
  onPasteTsv,
  tableReadOnly = false,
  isSelected = false,
  onCellMouseDown,
  onCellMouseEnter,
  selectionColIndex,
  onCellNavigate,
  onPasteDenied,
}: {
  row: T
  rowIndex: number
  column: ExcelColumn<T>
  colIndex: number
  onCommit: (rowIndex: number, key: keyof T & string, val: unknown) => void
  onCellFocus: (rowIndex: number, colIndex: number) => void
  onPasteTsv: (text: string, rowIndex: number, colIndex: number) => void
  tableReadOnly?: boolean
  isSelected?: boolean
  onCellMouseDown?: (e: ReactMouseEvent, rowIndex: number, colIndex: number) => void
  onCellMouseEnter?: (rowIndex: number, colIndex: number) => void
  selectionColIndex: number
  onCellNavigate: (rowIndex: number, colIndex: number, direction: 'up' | 'down' | 'left' | 'right') => void
  onPasteDenied?: () => void
}) {
  const val = row[column.key]
  const [isFocused, setIsFocused] = useState(false)
  const [localVal, setLocalVal] = useState<string>(
    val !== null && val !== undefined ? String(val) : '',
  )

  useEffect(() => {
    setLocalVal(val !== null && val !== undefined ? String(val) : '')
  }, [val])

  const commit = useCallback(() => {
    setIsFocused(false)
    if (resolveReadOnly(column, row) || tableReadOnly) return

    const raw = localVal.trim()
    let finalVal: unknown = raw

    if (column.parser) {
      finalVal = column.parser(raw, val)
    } else if (column.type === 'number' || (column.type as string) === 'currency') {
      if (raw === '') {
        finalVal = ''
      } else {
        const parsedNum = parseNumberValue(raw)
        finalVal = parsedNum !== null ? parsedNum : raw
      }
    }

    if (finalVal !== val) {
      onCommit(rowIndex, column.key, finalVal)
    }
  }, [column, localVal, onCommit, row, rowIndex, tableReadOnly, val])

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (column.multiline && e.shiftKey) return
      e.preventDefault()
      e.currentTarget.blur()
      onCellNavigate(rowIndex, selectionColIndex, e.shiftKey ? 'up' : 'down')
    } else if (e.key === 'Tab') {
      e.preventDefault()
      e.currentTarget.blur()
      onCellNavigate(rowIndex, selectionColIndex, e.shiftKey ? 'left' : 'right')
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      if (column.multiline && e.currentTarget.selectionStart !== e.currentTarget.selectionEnd) return
      e.preventDefault()
      e.currentTarget.blur()
      onCellNavigate(rowIndex, selectionColIndex, e.key === 'ArrowUp' ? 'up' : 'down')
    } else if (e.key === 'Escape') {
      setLocalVal(val !== null && val !== undefined ? String(val) : '')
      e.currentTarget.blur()
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData('text/plain')
    e.preventDefault()
    if (tableReadOnly) { onPasteDenied?.(); return }
    onPasteTsv(text, rowIndex, colIndex)
  }

  const isColReadOnly = resolveReadOnly(column, row)
  const isReadOnly = isColReadOnly || tableReadOnly
  const selectionBg = isSelected ? 'bg-sky-100/80 ring-1 ring-inset ring-sky-300' : ''
  const notice = column.cellNotice?.(row)
  const cellError = column.cellError?.(row)
  const noticeStyle: CSSProperties | undefined = cellError ? {
    backgroundColor: '#fee2e2',
    boxShadow: 'inset 0 0 0 1px #dc2626',
  } : notice ? {
    backgroundColor: '#fff8dc',
    backgroundImage: 'linear-gradient(135deg, transparent 0 88%, #dc2626 88% 100%)',
  } : undefined

  const isNumOrCur =
    column.type === 'number' || (column.type as string) === 'currency'

  if (column.type === 'select') {
    const options = column.options ?? []
    const selectValue = val == null ? '' : String(val)
    if (isReadOnly) return <div className={`min-h-[32px] px-2.5 py-1.5 text-xs text-slate-700 ${selectionBg}`} title={cellError ?? notice ?? options.find((option) => option.value === selectValue)?.label ?? ''}>{options.find((option) => option.value === selectValue)?.label ?? ''}</div>
    return <select
      aria-label={column.label}
      value={selectValue}
      onFocus={() => onCellFocus(rowIndex, selectionColIndex)}
      onChange={(event) => {
        const raw = event.currentTarget.value
        const parsed = column.parser ? column.parser(raw, val) : raw
        if (parsed !== val) onCommit(rowIndex, column.key, parsed)
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') event.currentTarget.blur()
        else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          try { event.currentTarget.showPicker() } catch { event.currentTarget.click() }
        }
      }}
      onPaste={(event) => { const text = event.clipboardData.getData('text/plain'); event.preventDefault(); if (tableReadOnly) { onPasteDenied?.(); return }; onPasteTsv(text, rowIndex, colIndex) }}
      onMouseEnter={() => onCellMouseEnter?.(rowIndex, colIndex)}
      title={cellError ?? notice ?? undefined}
      style={noticeStyle}
      className={`w-full min-h-[32px] px-2 py-1 text-xs bg-transparent outline-none focus:bg-sky-50/20 focus:ring-1 focus:ring-sky-500 ${selectionBg}`}
    >
      <option value="">Chọn…</option>
      {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
    </select>
  }

  if (isReadOnly) {
    if (column.renderCell) {
      return (
        <div
          className={`px-2.5 py-1.5 text-xs text-slate-700 select-none truncate min-h-[32px] ${selectionBg} ${
            column.align === 'right'
              ? 'text-right font-mono'
              : column.align === 'center'
              ? 'text-center'
              : 'text-left'
          }`}
          title={cellError ?? notice ?? undefined}
          style={noticeStyle}
          onMouseDown={(e) => onCellMouseDown?.(e, rowIndex, colIndex)}
          onMouseEnter={() => onCellMouseEnter?.(rowIndex, colIndex)}
        >
          {column.renderCell(val, row, rowIndex)}
        </div>
      )
    }

    const displayText = column.formatter
      ? column.formatter(val, row, rowIndex)
      : val !== null && val !== undefined
      ? isNumOrCur && typeof val === 'number'
        ? val.toLocaleString('en-US')
        : String(val)
      : ''

    return (
      <div
        className={`px-2.5 py-1.5 text-xs text-slate-700 select-none truncate min-h-[32px] ${selectionBg} ${
          column.align === 'right'
            ? 'text-right font-mono'
            : column.align === 'center'
            ? 'text-center'
            : 'text-left'
        }`}
          title={cellError ?? column.titleFormatter?.(val, row, rowIndex) ?? displayText}
          style={noticeStyle}
        onMouseDown={(e) => onCellMouseDown?.(e, rowIndex, colIndex)}
        onMouseEnter={() => onCellMouseEnter?.(rowIndex, colIndex)}
      >
        {displayText}
      </div>
    )
  }

  const displayFormatted =
    !isFocused && column.formatter
      ? column.formatter(val, row, rowIndex)
      : !isFocused && isNumOrCur && typeof val === 'number'
      ? val.toLocaleString('en-US')
      : localVal

  return (
    <>
    {column.type === 'date' ? <DateInput
      value={val == null ? '' : String(val).slice(0, 10)}
      onChange={(next) => onCommit(rowIndex, column.key, column.parser ? column.parser(next, val) : next || null)}
      onPaste={handlePaste}
      title={val ? formatDateFull(String(val).slice(0, 10)) : undefined}
      aria-label={column.label}
      className="w-full border-transparent"
    /> : column.multiline ? <textarea
      value={isFocused ? localVal : displayFormatted}
      placeholder={column.placeholder}
      rows={1}
      onFocus={() => { setIsFocused(true); onCellFocus(rowIndex, selectionColIndex) }}
      onChange={(e) => { setLocalVal(e.target.value); column.onInputChange?.(e.target.value, row, rowIndex) }}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      title={cellError ?? notice ?? undefined}
      style={noticeStyle}
      onMouseDown={(e) => onCellMouseDown?.(e, rowIndex, colIndex)}
      onMouseEnter={() => onCellMouseEnter?.(rowIndex, colIndex)}
      className={`w-full min-h-[32px] px-2.5 py-1 text-xs text-slate-800 bg-transparent outline-none transition-colors border border-transparent focus:border-sky-500 focus:bg-sky-50/20 focus:ring-1 focus:ring-sky-500 rounded-none resize-y ${selectionBg}`}
    /> : <input
      type="text"
      value={isFocused ? localVal : displayFormatted}
      placeholder={column.placeholder}
      onFocus={() => {
        if (column.inputDisplay && column.formatter) setLocalVal(column.formatter(val, row, rowIndex))
        setIsFocused(true)
        onCellFocus(rowIndex, selectionColIndex)
      }}
      onChange={(e) => { setLocalVal(e.target.value); column.onInputChange?.(e.target.value, row, rowIndex) }}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      list={column.suggestions ? `excel-suggestions-${column.key}-${rowIndex}` : undefined}
      title={cellError ?? notice ?? undefined}
      style={noticeStyle}
      onMouseDown={(e) => onCellMouseDown?.(e, rowIndex, colIndex)}
      onMouseEnter={() => onCellMouseEnter?.(rowIndex, colIndex)}
      className={`w-full min-h-[32px] px-2.5 py-1 text-xs text-slate-800 bg-transparent outline-none transition-colors border border-transparent focus:border-sky-500 focus:bg-sky-50/20 focus:ring-1 focus:ring-sky-500 rounded-none ${selectionBg} ${
        column.align === 'right'
          ? 'text-right font-mono'
          : column.align === 'center'
          ? 'text-center font-mono'
          : 'text-left'
      }`}
    />}
    {column.suggestions && <datalist id={`excel-suggestions-${column.key}-${rowIndex}`}>{column.suggestions(row).map((suggestion) => <option key={suggestion} value={suggestion} />)}</datalist>}
    </>
  )
}

export type ExcelFilterOption = { label: string; values: string[] }

/** Groups raw filter values under the labels users see in the filter menu. */
export function filterOptionsForColumn<T extends Record<string, unknown>>(
  data: T[],
  column: ExcelColumn<T>,
): ExcelFilterOption[] {
  const valuesByLabel = new Map<string, Set<string>>()
  for (const row of data) {
    const raw = row[column.key]
    const rawValue = raw !== null && raw !== undefined ? String(raw) : ''
    const label = column.filterLabel?.(row) ?? rawValue
    const values = valuesByLabel.get(label) ?? new Set<string>()
    values.add(rawValue)
    valuesByLabel.set(label, values)
  }
  return [...valuesByLabel.entries()]
    .map(([label, values]) => ({ label, values: [...values] }))
    .sort((a, b) => {
      if (a.label === '') return 1
      if (b.label === '') return -1
      if (column.type === 'number' || column.type === 'currency') {
        const nA = parseNumberValue(a.label)
        const nB = parseNumberValue(b.label)
        if (nA !== null && nB !== null) return nA - nB
      }
      return a.label.localeCompare(b.label, 'vi', { numeric: true, sensitivity: 'base' })
    })
}

export function ExcelFilterPopover<T extends Record<string, unknown>>({
  column,
  data,
  filterItem,
  anchorRect,
  onApply,
  onClose,
}: {
  column: ExcelColumn<T>
  data: T[]
  filterItem?: FilterItem
  anchorRect: DOMRect
  onApply: (item: FilterItem | null) => void
  onClose: () => void
}) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const isNumOrCur = column.type === 'number' || (column.type as string) === 'currency'

  const filterOptions = useMemo(
    () => filterOptionsForColumn(data, column),
    [data, column],
  )
  const allRawValues = useMemo(
    () => new Set(filterOptions.flatMap((option) => option.values)),
    [filterOptions],
  )

  // Range min/max
  const [minVal, setMinVal] = useState<string>(
    filterItem?.range?.min != null ? String(filterItem.range.min) : '',
  )
  const [maxVal, setMaxVal] = useState<string>(
    filterItem?.range?.max != null ? String(filterItem.range.max) : '',
  )

  const isRangeMode = isNumOrCur && (minVal !== '' || maxVal !== '')

  // Checkbox values selection
  const [selectedVals, setSelectedVals] = useState<Set<string>>(() => {
    if (filterItem?.values) {
      return new Set(filterItem.values)
    }
    return new Set(allRawValues)
  })

  // Search filter inside dropdown
  const [searchQuery, setSearchQuery] = useState('')

  const filteredDistinctValues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return filterOptions
    return filterOptions.filter((option) => {
      const display = option.label === '' ? '(trống)' : option.label
      return display.toLowerCase().includes(q)
    })
  }, [filterOptions, searchQuery])

  // Close on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  const handleToggleVal = (option: ExcelFilterOption) => {
    setSelectedVals((prev) => {
      const next = new Set(prev)
      const isSelected = option.values.every((value) => next.has(value))
      for (const value of option.values) {
        if (isSelected) next.delete(value)
        else next.add(value)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    setSelectedVals(new Set(allRawValues))
  }

  const handleDeselectAll = () => {
    setSelectedVals(new Set())
  }

  const handleClearFilter = () => {
    onApply(null)
  }

  const handleApply = () => {
    if (isRangeMode) {
      const minNum = minVal !== '' ? parseNumberValue(minVal) : null
      const maxNum = maxVal !== '' ? parseNumberValue(maxVal) : null
      onApply({
        range: { min: minNum, max: maxNum },
      })
      return
    }

    // Đang gõ tìm kiếm (kiểu Excel): chỉ lọc theo các giá trị đang hiện và được tích, các giá trị bị ẩn bởi ô tìm kiếm không được tính.
    if (searchQuery.trim() !== '') {
      const visibleChecked = new Set(filteredDistinctValues.flatMap((option) => option.values).filter((value) => selectedVals.has(value)))
      if (visibleChecked.size === 0) {
        onApply({ values: new Set<string>() })
        return
      }
      onApply({ values: visibleChecked })
      return
    }

    if ([...allRawValues].every((value) => selectedVals.has(value))) {
      onApply(null)
    } else {
      onApply({ values: selectedVals })
    }
  }

  const popoverWidth = 260
  const top = anchorRect.bottom + 4
  const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth
  const left = Math.max(8, Math.min(anchorRect.left, viewportWidth - popoverWidth - 16))

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${popoverWidth}px`,
        zIndex: 9999,
      }}
      className="rounded-md border border-slate-300 bg-white shadow-xl text-xs font-sans ring-1 ring-black/5"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2">
        <span className="font-semibold text-slate-700 truncate">Lọc: {column.label}</span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Đóng bộ lọc"
          className="text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {isNumOrCur && (
        <div className="p-2.5 border-b border-slate-200 bg-slate-50/50">
          <div className="text-[11px] font-semibold text-slate-700 mb-1.5">Lọc theo khoảng:</div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Từ</label>
              <input
                type="number"
                value={minVal}
                onChange={(e) => setMinVal(e.target.value)}
                placeholder="Min"
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded bg-white focus:border-sky-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-0.5">Đến</label>
              <input
                type="number"
                value={maxVal}
                onChange={(e) => setMaxVal(e.target.value)}
                placeholder="Max"
                className="w-full text-xs px-2 py-1 border border-slate-300 rounded bg-white focus:border-sky-500 focus:outline-none"
              />
            </div>
          </div>
          {(minVal !== '' || maxVal !== '') && (
            <button
              type="button"
              onClick={() => {
                setMinVal('')
                setMaxVal('')
              }}
              className="mt-1.5 text-[11px] text-sky-600 hover:text-sky-800 underline cursor-pointer"
            >
              Xóa khoảng
            </button>
          )}
        </div>
      )}

      <div className={isRangeMode ? 'opacity-40 pointer-events-none' : ''}>
        {isRangeMode && (
          <div className="px-3 py-1 bg-amber-50 text-[10px] text-amber-700 border-b border-amber-100">
            Đang lọc khoảng — danh sách giá trị tạm tắt
          </div>
        )}

        <div className="p-2 border-b border-slate-100 flex items-center gap-1.5">
          <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Tìm giá trị..."
            className="w-full text-xs border-0 outline-none bg-transparent placeholder:text-slate-400"
          />
        </div>

        <div className="flex items-center justify-between px-3 py-1.5 text-[11px] border-b border-slate-100 bg-slate-50/70">
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sky-600 hover:text-sky-800 hover:underline cursor-pointer"
          >
            Chọn tất cả
          </button>
          <button
            type="button"
            onClick={handleDeselectAll}
            className="text-slate-500 hover:text-slate-700 hover:underline cursor-pointer"
          >
            Bỏ chọn tất cả
          </button>
        </div>

        <div className="max-h-44 overflow-y-auto px-2 py-1 space-y-0.5">
          {filteredDistinctValues.length === 0 ? (
            <div className="py-3 text-center text-slate-400 text-[11px]">Không tìm thấy</div>
          ) : (
            filteredDistinctValues.map((option) => {
              const isChecked = option.values.every((value) => selectedVals.has(value))
              const displayLabel = option.label === '' ? '(Trống)' : option.label
              return (
                <label
                  key={option.label}
                  className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-slate-50 cursor-pointer text-xs text-slate-700 select-none"
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleVal(option)}
                    className="rounded border-slate-300 text-sky-600 focus:ring-sky-500 h-3.5 w-3.5"
                  />
                  <span className={`truncate ${option.label === '' ? 'italic text-slate-400' : ''}`}>
                    {displayLabel}
                  </span>
                </label>
              )
            })
          )}
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-3 py-2">
        <button
          type="button"
          onClick={handleClearFilter}
          className="text-xs text-slate-600 hover:text-red-600 transition-colors cursor-pointer"
        >
          Xóa lọc
        </button>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1 text-xs border border-slate-300 rounded hover:bg-slate-100 text-slate-700 transition-colors cursor-pointer"
          >
            Hủy
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="px-3 py-1 text-xs bg-sky-600 hover:bg-sky-700 text-white rounded font-medium shadow-xs transition-colors cursor-pointer"
          >
            Áp dụng
          </button>
        </div>
      </div>
    </div>
  )
}

export function ExcelEditableTable<T extends Record<string, unknown>>({
  columns,
  data,
  onChange,
  onCopy: onCopyOverride,
  onPasteDenied,
  onRowContextMenu,
  onRowActionMenu,
  onRowReorder,
  onRowQuickAction,
  getRowQuickActionDisabledReason,
  onRowFocus,
  getRowClassName,
  emptyRowFactory,
  regenerateId,
  rowKey,
  readOnly = false,
  onNewRow,
  className = '',
  addRowLabel = 'Thêm dòng mới',
  emptyMessage = 'Chưa có dòng dữ liệu nào.',
  showActions = true,
  showAddRowButton = true,
  headerExtra,
  storageKey,
  widthStorageKey,
  resizableColumns = false,
  summaryRow,
  variant = 'default',
  hiddenColumnsStorageKey,
  defaultHiddenColumnKeys = [],
  columnVisibilityPresets = [],
  fill = false,
  rowHeight = 34,
  density = 'compact',
}: ExcelEditableTableProps<T>) {
  const [hiddenColumnsOpen, setHiddenColumnsOpen] = useState(false)
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState<Set<string>>(() => new Set())
  const defaultHiddenColumnSignature = defaultHiddenColumnKeys.join('\u001f')
  const resolvedHiddenColumnsStorageKey = hiddenColumnsStorageKey ?? widthStorageKey?.replace(/\.widths(?=\.|$)/, '.hidden')
  const visibleColumns = useMemo(() => columns.filter((column) => !column.hidden && !hiddenColumnKeys.has(column.key)), [columns, hiddenColumnKeys])
  const allColumnKeySignature = columns.map((column) => column.key).join('\u001f')
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [resizingColumn, setResizingColumn] = useState<{ key: string; startX: number; startWidth: number } | null>(null)
  const [resizeGuideX, setResizeGuideX] = useState<number | null>(null)
  const [columnMenu, setColumnMenu] = useState<{ key: string; x: number; y: number } | null>(null)
  const actionColumnKey = '__row_actions__'
  const actionColumn = { key: actionColumnKey, stickyRight: 0, width: 144 }
  const resolveColumnWidth = useCallback((column: ExcelColumn<T>) => {
    const savedWidth = columnWidths[column.key]
    if (savedWidth !== undefined) return savedWidth
    const configuredWidth = typeof column.width === 'number' ? column.width : undefined
    if (variant !== 'sheet') return configuredWidth
    const readableHeaderWidth = Math.min(260, Math.max(76, Math.ceil(column.label.length / 3) * 7 + 52))
    return Math.max(configuredWidth ?? Number(column.minWidth ?? 100), readableHeaderWidth)
  }, [columnWidths, variant])
  const layoutColumnWidths = useMemo(() => Object.fromEntries(visibleColumns.map((column) => [column.key, resolveColumnWidth(column) ?? Number(column.minWidth ?? 100)])), [resolveColumnWidth, visibleColumns])
  const stickyOffsets = useMemo(() => computeStickyOffsets(showActions ? [...visibleColumns, actionColumn] : visibleColumns, layoutColumnWidths), [showActions, visibleColumns, layoutColumnWidths])
  const sheetTableWidth = variant === 'sheet'
    ? [...visibleColumns.map((column) => Number(resolveColumnWidth(column) ?? column.minWidth ?? 100)), ...(showActions ? [144] : [])].reduce((total, width) => total + width, 0)
    : undefined
  const persistColumnWidths = useCallback((next: Record<string, number>) => {
    setColumnWidths(next)
    if (widthStorageKey) {
      try { localStorage.setItem(widthStorageKey, JSON.stringify(next)) } catch { /* storage can be disabled */ }
    }
  }, [widthStorageKey])
  useEffect(() => {
    const visibleColumnKeys = allColumnKeySignature ? allColumnKeySignature.split('\u001f') : []
    let restored: Record<string, number> = {}
    if (widthStorageKey) {
      try { restored = readExcelColumnWidths(localStorage.getItem(widthStorageKey), visibleColumnKeys) }
      catch { restored = {} }
    }
    setColumnWidths((current) => retainExcelColumnWidths(current, restored))
  }, [widthStorageKey, allColumnKeySignature])
  useEffect(() => {
    const defaults = defaultHiddenColumnSignature ? defaultHiddenColumnSignature.split('\u001f') : []
    if (!resolvedHiddenColumnsStorageKey) { setHiddenColumnKeys(new Set(defaults)); return }
    try {
      const saved = localStorage.getItem(resolvedHiddenColumnsStorageKey)
      setHiddenColumnKeys(new Set(saved ? JSON.parse(saved) as string[] : defaults))
    } catch {
      setHiddenColumnKeys(new Set(defaults))
    }
  }, [resolvedHiddenColumnsStorageKey, defaultHiddenColumnSignature])
  useEffect(() => {
    if (!resizingColumn) return
    const move = (event: globalThis.MouseEvent) => { setResizeGuideX(event.clientX); persistColumnWidths({ ...columnWidths, [resizingColumn.key]: clampExcelColumnWidth(resizingColumn.startWidth + event.clientX - resizingColumn.startX) }) }
    const up = () => { setResizingColumn(null); setResizeGuideX(null) }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up, { once: true })
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up) }
  }, [columnWidths, persistColumnWidths, resizingColumn])
  const visibleColumnIndices = useMemo(() => visibleColumns.map((column) => columns.indexOf(column)), [columns, visibleColumns])
  const visibleColumnIndexMap = useMemo(() => new Map(visibleColumnIndices.map((original, index) => [original, index])), [visibleColumnIndices])
  // Sort state
  const [sortConfig, setSortConfig] = useState<SortItem[]>(() => {
    if (!storageKey) return []
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) return parsed
      }
    } catch {
      // Safe fallback
    }
    return []
  })

  // Synchronize sortConfig when storageKey changes
  useEffect(() => {
    if (!storageKey) {
      setSortConfig([])
      return
    }
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          setSortConfig(parsed)
          return
        }
      }
      setSortConfig([])
    } catch {
      setSortConfig([])
    }
  }, [storageKey])

  const updateSortConfig = useCallback(
    (newConfig: SortItem[]) => {
      setSortConfig(newConfig)
      if (storageKey) {
        try {
          if (newConfig.length === 0) {
            localStorage.removeItem(storageKey)
          } else {
            localStorage.setItem(storageKey, JSON.stringify(newConfig))
          }
        } catch {
          // Safe fallback
        }
      }
    },
    [storageKey],
  )

  // Filter state (transient, never saved to storageKey)
  const [filterState, setFilterState] = useState<FilterState>({})
  const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null)
  const [filterAnchorRect, setFilterAnchorRect] = useState<DOMRect | null>(null)

  const [focusedCell, setFocusedCell] = useState<{
    rowIndex: number
    colIndex: number
  } | null>(null)

  // Cell range selection (Excel-like drag / Shift+click)
  const [selAnchor, setSelAnchor] = useState<CellPos | null>(null)
  const [selFocus, setSelFocus] = useState<CellPos | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  // Full-row selection via STT / row gutter (Ctrl/Cmd multi, Shift range)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())
  const [rowAnchor, setRowAnchor] = useState<number | null>(null)
  const [draggedRowIndex, setDraggedRowIndex] = useState<number | null>(null)
  const [dropRowIndex, setDropRowIndex] = useState<number | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const [headerHeight, setHeaderHeight] = useState(0)
  useEffect(() => {
    if (variant !== 'sheet') return
    const header = containerRef.current?.querySelector('thead')
    if (!header) return
    const update = () => {
      const nextHeight = header.getBoundingClientRect().height
      setHeaderHeight((current) => current === nextHeight ? current : nextHeight)
    }
    const observer = new ResizeObserver(update)
    observer.observe(header)
    update()
    return () => observer.disconnect()
  }, [variant])

  const clearSelection = useCallback(() => {
    setSelAnchor(null)
    setSelFocus(null)
    setSelectedRows(new Set())
    setRowAnchor(null)
  }, [])

  // Reset selection and focused cell whenever sortConfig, filterState, or data.length changes
  useEffect(() => {
    clearSelection()
    setFocusedCell(null)
  }, [sortConfig, filterState, data.length, clearSelection])

  // Compute visible rows mapping to original indices in data
  const visibleRows = useMemo(
    () => computeVisibleRows(data, filterState, sortConfig, visibleColumns),
    [data, filterState, sortConfig, visibleColumns],
  )
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const shouldVirtualize = fill && visibleRows.length > 200
  const rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => tableScrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
    enabled: shouldVirtualize,
  })
  const virtualItems = shouldVirtualize ? rowVirtualizer.getVirtualItems() : []
  const renderedRowIndexes = shouldVirtualize
    ? virtualItems.map((item) => item.index)
    : visibleRows.map((_, index) => index)
  const topSpacerHeight = shouldVirtualize ? (virtualItems[0]?.start ?? 0) : 0
  const lastVirtualItem = virtualItems[virtualItems.length - 1]
  const bottomSpacerHeight = shouldVirtualize && lastVirtualItem
    ? Math.max(0, rowVirtualizer.getTotalSize() - lastVirtualItem.end)
    : 0

  const visibleIndices = useMemo(
    () => visibleRows.map((v) => v.originalIndex),
    [visibleRows],
  )

  const handleCellCommit = useCallback(
    (visibleRowIndex: number, key: keyof T & string, val: unknown) => {
      const next = commitCellAtVisible(data, visibleRows, visibleRowIndex, key, val)
      if (next !== data) onChange(next)
    },
    [data, onChange, visibleRows],
  )

  const handleCellFocus = useCallback((rowIndex: number, colIndex: number) => {
    setFocusedCell({ rowIndex, colIndex })
    const row = visibleRows[rowIndex]?.row
    if (row) onRowFocus?.(row)
  }, [onRowFocus, visibleRows])

  const focusGridCell = useCallback((rowIndex: number, colIndex: number, enterEdit = false) => {
    const row = Math.max(0, Math.min(visibleRows.length - 1, rowIndex))
    const col = Math.max(0, Math.min(visibleColumns.length - 1, colIndex))
    setFocusedCell({ rowIndex: row, colIndex: col })
    setSelAnchor({ row, col })
    setSelFocus({ row, col })
    requestAnimationFrame(() => {
      const cell = containerRef.current?.querySelector<HTMLElement>(`[data-excel-row="${row}"][data-excel-col="${col}"]`)
      const editor = cell?.querySelector<HTMLInputElement | HTMLTextAreaElement>('input:not(:disabled), textarea:not(:disabled), select:not(:disabled)')
      if (enterEdit && editor) editor.focus()
      else containerRef.current?.focus({ preventScroll: true })
      cell?.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    })
  }, [visibleColumns.length, visibleRows.length])

  const navigateGridCell = useCallback((row: number, col: number, direction: 'up' | 'down' | 'left' | 'right') => {
    focusGridCell(row + (direction === 'up' ? -1 : direction === 'down' ? 1 : 0), col + (direction === 'left' ? -1 : direction === 'right' ? 1 : 0), true)
  }, [focusGridCell])

  const handlePasteTsv = useCallback(
      (tsv: string, startVisibleRow: number, startCol: number) => {
    if (readOnly) { onPasteDenied?.(); return }
      if (selAnchor && selFocus) {
        const expanded = expandScalarTsvToRange(tsv, selAnchor, selFocus)
        if (expanded !== tsv) {
          tsv = expanded
          startVisibleRow = Math.min(selAnchor.row, selFocus.row)
          startCol = Math.min(selAnchor.col, selFocus.col)
        }
      }
      const next = applyTsvToData({
        tsv,
        data,
        columns: visibleColumns,
        emptyRowFactory,
        startRow: startVisibleRow,
        startCol: visibleColumns.findIndex((_column, index) => visibleColumnIndices[index] === startCol),
        visibleIndices,
      })
      onChange(next)
    },
    [visibleColumns, visibleColumnIndices, data, emptyRowFactory, onChange, onPasteDenied, readOnly, selAnchor, selFocus, visibleIndices],
  )

  const handleCopy = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    if (onCopyOverride) { onCopyOverride(e); return }
    const target = e.target as HTMLInputElement | HTMLTextAreaElement
    if ((target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') && target.selectionStart !== target.selectionEnd) return
    if (!selAnchor || !selFocus) return
    const { r0, r1, c0, c1 } = normalizeCellRange(selAnchor, selFocus)
    const matrix: string[][] = []
    for (let r = r0; r <= r1; r++) {
      const visible = visibleRows[r]?.row
      if (!visible) continue
      const cells: string[] = []
      for (let c = c0; c <= c1; c++) {
        const column = visibleColumns[c]
        if (!column) { cells.push(''); continue }
        const value = visible[column.key]
        const text = column.formatter
          ? column.formatter(value, visible, r)
          : value == null ? '' : typeof value === 'number' && (column.type === 'number' || (column.type as string) === 'currency')
            ? value.toLocaleString('en-US') : String(value)
        cells.push(text)
      }
      matrix.push(cells)
    }
    if (matrix.length) {
      e.preventDefault()
      e.clipboardData.setData('text/plain', serializeTsv(matrix))
    }
  }, [visibleColumns, onCopyOverride, selAnchor, selFocus, visibleRows])

  const handleContainerPaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return
      const text = e.clipboardData.getData('text/plain')
      if (!text) return
      e.preventDefault()
      if (readOnly) { onPasteDenied?.(); return }
      const targetRow = focusedCell?.rowIndex ?? (visibleRows.length > 0 ? visibleRows.length - 1 : 0)
      const targetCol = focusedCell ? (visibleColumnIndices[focusedCell.colIndex] ?? focusedCell.colIndex) : (isColumnAlwaysReadOnly(visibleColumns[0]) ? (visibleColumnIndices[1] ?? 1) : (visibleColumnIndices[0] ?? 0))
      handlePasteTsv(text, targetRow, targetCol)
    },
    [visibleColumns, visibleColumnIndices, focusedCell, handlePasteTsv, onPasteDenied, readOnly, visibleRows.length],
  )

  const handleCellMouseDown = useCallback(
    (e: ReactMouseEvent, rowIndex: number, colIndex: number) => {
      if (e.button !== 0) return
      containerRef.current?.focus({ preventScroll: true })
      setSelectedRows(new Set())
      setRowAnchor(null)

      if (e.shiftKey && selAnchor) {
        e.preventDefault()
        setSelFocus({ row: rowIndex, col: colIndex })
        setFocusedCell({ rowIndex, colIndex })
        const row = visibleRows[rowIndex]?.row
        if (row) onRowFocus?.(row)
        return
      }

      setSelAnchor({ row: rowIndex, col: colIndex })
      setSelFocus({ row: rowIndex, col: colIndex })
      setFocusedCell({ rowIndex, colIndex })
      setIsDragging(true)
      const row = visibleRows[rowIndex]?.row
      if (row) onRowFocus?.(row)
    },
    [onRowFocus, selAnchor, visibleRows],
  )

  const handleCellMouseEnter = useCallback(
    (rowIndex: number, colIndex: number) => {
      if (!isDragging || !selAnchor) return
      setSelFocus({ row: rowIndex, col: colIndex })
    },
    [isDragging, selAnchor],
  )

  useEffect(() => {
    if (!isDragging) return
    const onUp = () => setIsDragging(false)
    window.addEventListener('mouseup', onUp)
    return () => window.removeEventListener('mouseup', onUp)
  }, [isDragging])

  const handleRowHeaderClick = useCallback(
    (e: ReactMouseEvent, rowIndex: number) => {
      e.preventDefault()
      e.stopPropagation()
      setSelAnchor(null)
      setSelFocus(null)

      if (e.ctrlKey || e.metaKey) {
        setSelectedRows((prev) => {
          const next = new Set(prev)
          if (next.has(rowIndex)) next.delete(rowIndex)
          else next.add(rowIndex)
          return next
        })
        setRowAnchor(rowIndex)
        return
      }

      if (e.shiftKey && rowAnchor !== null) {
        const r0 = Math.min(rowAnchor, rowIndex)
        const r1 = Math.max(rowAnchor, rowIndex)
        const next = new Set<number>()
        for (let i = r0; i <= r1; i++) next.add(i)
        setSelectedRows(next)
        return
      }

      setSelectedRows(new Set([rowIndex]))
      setRowAnchor(rowIndex)
    },
    [rowAnchor],
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const targetTag = (e.target as HTMLElement).tagName
      const typing = targetTag === 'INPUT' || targetTag === 'TEXTAREA' || targetTag === 'SELECT'
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') return
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        if (!readOnly) onChange(addTableRow(data, onNewRow ? onNewRow(emptyRowFactory()) : emptyRowFactory()))
        return
      }
      if (!typing && focusedCell && (e.key === 'F2' || e.key === 'Enter')) {
        e.preventDefault()
        focusGridCell(focusedCell.rowIndex, focusedCell.colIndex, true)
        return
      }
      if (!typing && focusedCell && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        const row = focusedCell.rowIndex + (e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0)
        const col = focusedCell.colIndex + (e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0)
        e.preventDefault()
        const nextRow = Math.max(0, Math.min(visibleRows.length - 1, row))
        const nextCol = Math.max(0, Math.min(visibleColumns.length - 1, col))
        if (e.shiftKey && selAnchor) { setFocusedCell({ rowIndex: nextRow, colIndex: nextCol }); setSelFocus({ row: nextRow, col: nextCol }); containerRef.current?.focus({ preventScroll: true }) }
        else focusGridCell(nextRow, nextCol)
        return
      }
      if (e.key !== 'Delete' && e.key !== 'Backspace') return
      if (readOnly) return

      if (typing) {
        const hasRange =
          selAnchor &&
          selFocus &&
          (selAnchor.row !== selFocus.row || selAnchor.col !== selFocus.col)
        if (!hasRange && selectedRows.size === 0) return
        ;(e.target as HTMLElement).blur()
      }

      if (selectedRows.size > 0) {
        e.preventDefault()
        const next = deleteVisibleRows(data, visibleRows, selectedRows)
        if (next !== data) onChange(next)
        clearSelection()
        return
      }

      if (selAnchor && selFocus) {
        e.preventDefault()
        const next = clearVisibleCellsInRange(data, visibleRows, visibleColumns, selAnchor, selFocus)
        if (next !== data) onChange(next)
      }
    },
    [
      clearSelection,
      visibleColumns,
      columns,
      data,
      emptyRowFactory,
      onNewRow,
      focusGridCell,
      onChange,
      readOnly,
      selAnchor,
      selFocus,
      selectedRows,
      visibleRows,
    ],
  )

  const handleAddRow = () => {
    onChange(addTableRow(data, onNewRow ? onNewRow(emptyRowFactory()) : emptyRowFactory()))
  }

  const handleInsertBelow = (visibleRowIndex: number) => {
    const next = insertVisibleRow(data, visibleRows, visibleRowIndex, emptyRowFactory())
    if (next !== data) onChange(next)
  }

  const handleDeleteRow = (visibleRowIndex: number) => {
    const next = deleteVisibleRows(data, visibleRows, [visibleRowIndex])
    if (next !== data) onChange(next)
    clearSelection()
  }

  const handleMoveUp = (visibleRowIndex: number) => {
    const next = swapVisibleRows(data, visibleRows, visibleRowIndex, 'up', sortConfig)
    if (next !== data) onChange(next)
  }

  const handleMoveDown = (visibleRowIndex: number) => {
    const next = swapVisibleRows(data, visibleRows, visibleRowIndex, 'down', sortConfig)
    if (next !== data) onChange(next)
  }

  const handleDuplicate = (visibleRowIndex: number) => {
    const next = duplicateVisibleRow(data, visibleRows, visibleRowIndex, regenerateId)
    if (next !== data) onChange(next)
  }

  // Header click sort handler (cycles asc -> desc -> none, Shift for multi-sort)
  const handleSortClick = (key: string, isShift: boolean) => {
    let next: SortItem[]
    if (isShift) {
      const existingIdx = sortConfig.findIndex((s) => s.key === key)
      if (existingIdx === -1) {
        next = [...sortConfig, { key, direction: 'asc' }]
      } else {
        const cur = sortConfig[existingIdx]
        if (cur.direction === 'asc') {
          next = sortConfig.map((s, idx) => (idx === existingIdx ? { ...s, direction: 'desc' as const } : s))
        } else {
          next = sortConfig.filter((_, idx) => idx !== existingIdx)
        }
      }
    } else {
      if (sortConfig.length === 1 && sortConfig[0].key === key) {
        if (sortConfig[0].direction === 'asc') {
          next = [{ key, direction: 'desc' }]
        } else {
          next = []
        }
      } else {
        next = [{ key, direction: 'asc' }]
      }
    }
    updateSortConfig(next)
  }

  const handleOpenFilter = (key: string, e: ReactMouseEvent) => {
    e.stopPropagation()
    if (activeFilterCol === key) {
      setActiveFilterCol(null)
      setFilterAnchorRect(null)
    } else {
      setActiveFilterCol(key)
      setFilterAnchorRect(e.currentTarget.getBoundingClientRect())
    }
  }

  const handleClearAllFilters = () => {
    setFilterState({})
    setActiveFilterCol(null)
    setFilterAnchorRect(null)
  }

  const resetColumnWidth = (key?: string) => {
    const next = { ...columnWidths }
    if (key) delete next[key]
    else Object.keys(next).forEach((columnKey) => delete next[columnKey])
    persistColumnWidths(next)
    setColumnMenu(null)
  }

  const hasCellSelection = Boolean(selAnchor && selFocus)
  const hasRowSelection = selectedRows.size > 0

  const activeFilterColumns = visibleColumns.filter((c) => isColumnFilterActive(c.key, filterState, data))
  const isFiltering = activeFilterColumns.length > 0
  const lastPinnedColumnKey = [...visibleColumns].reverse().find((column) => column.stickyLeft !== undefined)?.key

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      data-fill={fill ? 'true' : undefined}
      data-density={fill ? density : undefined}
      style={fill ? { '--excel-fill-row-height': `${rowHeight}px` } as CSSProperties : undefined}
      className={`excel-editable-table-container ${variant === 'sheet' ? 'excel-sheet-variant' : ''} ${fill ? 'h-full min-h-0' : ''} flex flex-col rounded-[var(--radius-md)] border border-slate-300 bg-white shadow-xs overflow-hidden outline-none focus:ring-1 focus:ring-sky-300 ${className}`}
      onPaste={handleContainerPaste}
      onCopy={handleCopy}
      onKeyDown={handleKeyDown}
    >
      {headerExtra && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/80 px-3 py-2 text-xs">
          {headerExtra}
        </div>
      )}

      {(isFiltering || sortConfig.length > 0) && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200 bg-amber-50/90 px-3 py-1.5 text-[11px] text-amber-900 font-medium">
          <div className="flex items-center gap-1.5">
            <Filter className="h-3 w-3 text-amber-600" />
            <span>
              {activeFilterColumns.length ? `Đang lọc ${activeFilterColumns.length} cột · hiện ${visibleRows.length}/${data.length} dòng` : `Đang sắp xếp ${sortConfig.length} cột`}
            </span>
          </div>
          <button
            type="button"
            onClick={() => isFiltering ? handleClearAllFilters() : updateSortConfig([])}
            className="text-amber-800 hover:text-amber-950 underline font-semibold cursor-pointer"
          >
            ✕ {isFiltering ? 'Bỏ lọc' : 'Bỏ sắp xếp'}
          </button>
          {sortConfig.length > 0 && <button type="button" onClick={() => updateSortConfig([])} className="text-amber-800 hover:text-amber-950 underline font-semibold cursor-pointer">Bỏ sắp xếp</button>}
        </div>
      )}

      {resizableColumns && <div className="excel-table-settings-bar flex items-center justify-end gap-3 border-b border-slate-200 bg-white px-3 py-1 text-[11px]">
        {isFiltering && <span>Đang lọc {activeFilterColumns.length} cột</span>}
        <div className="relative flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => resetColumnWidth()} className="h-6 px-2">↔ Đặt lại độ rộng</Button>
          <Button type="button" variant="ghost" size="sm" aria-expanded={hiddenColumnsOpen} onClick={() => setHiddenColumnsOpen((open) => !open)} className="h-6 px-2">Cột</Button>
          {hiddenColumnsOpen && <div role="dialog" aria-label="Ẩn hiện cột" className="absolute right-0 top-full z-50 mt-1 max-h-[70vh] w-72 overflow-auto rounded border border-slate-300 bg-white p-2 shadow-lg">
            <div className="mb-2 flex gap-2 border-b border-slate-200 pb-2">
              <button type="button" className="text-xs text-sky-700 underline" onClick={() => { const next = new Set(columns.filter((column) => !column.required).map((column) => column.key)); setHiddenColumnKeys(next); try { if (resolvedHiddenColumnsStorageKey) localStorage.setItem(resolvedHiddenColumnsStorageKey, JSON.stringify([...next])) } catch { /* storage can be disabled */ } }}>Ẩn tất cả</button>
              <button type="button" className="text-xs text-sky-700 underline" onClick={() => { setHiddenColumnKeys(new Set()); try { if (resolvedHiddenColumnsStorageKey) localStorage.setItem(resolvedHiddenColumnsStorageKey, JSON.stringify([])) } catch { /* storage can be disabled */ } }}>Hiện tất cả</button>
              {columnVisibilityPresets.map((preset) => <button key={preset.label} type="button" className="text-xs text-sky-700 underline" onClick={() => { const next = new Set(preset.keys); setHiddenColumnKeys(next); try { if (resolvedHiddenColumnsStorageKey) localStorage.setItem(resolvedHiddenColumnsStorageKey, JSON.stringify([...next])) } catch { /* storage can be disabled */ } }}>{preset.label}</button>)}
              <button type="button" className="ml-auto text-xs text-slate-600 underline" onClick={() => { const defaults = defaultHiddenColumnSignature ? defaultHiddenColumnSignature.split('\u001f') : []; const next = new Set(defaults); setHiddenColumnKeys(next); try { if (resolvedHiddenColumnsStorageKey) localStorage.removeItem(resolvedHiddenColumnsStorageKey) } catch { /* storage can be disabled */ } }}>Đặt lại</button>
            </div>
            {columns.map((column) => <label key={column.key} className="flex items-center gap-2 rounded px-1 py-1 text-xs hover:bg-slate-50">
              <input type="checkbox" checked={!hiddenColumnKeys.has(column.key)} disabled={column.required} onChange={(event) => {
                if (column.required) return
                const next = new Set(hiddenColumnKeys)
                if (event.currentTarget.checked) next.delete(column.key); else next.add(column.key)
                setHiddenColumnKeys(next)
                try { if (resolvedHiddenColumnsStorageKey) localStorage.setItem(resolvedHiddenColumnsStorageKey, JSON.stringify([...next])) } catch { /* storage can be disabled */ }
              }} />
              <span>{column.label}</span>{column.required && <span className="ml-auto text-slate-400">Bắt buộc</span>}
            </label>)}
          </div>}
        </div>
      </div>}

      {(hasCellSelection || hasRowSelection) && (
        <div className="flex flex-wrap items-center gap-2 border-b border-sky-100 bg-sky-50/70 px-3 py-1.5 text-[11px] text-sky-800">
          {hasRowSelection ? (
            <span>
              Đã chọn {selectedRows.size} dòng — nhấn Delete để xóa dòng
            </span>
          ) : (
            <span>
              Đã chọn vùng ô — nhấn Delete để xóa nội dung (không xóa dòng)
            </span>
          )}
          <button
            type="button"
            className="ml-auto text-sky-600 hover:text-sky-800 underline cursor-pointer"
            onClick={clearSelection}
          >
            Bỏ chọn
          </button>
        </div>
      )}

      <div ref={tableScrollRef} className={fill ? `excel-table-fill-scroll ${variant === 'sheet' ? 'excel-sheet-scroll' : ''}` : variant === 'sheet' ? 'excel-sheet-scroll' : 'overflow-x-auto'}>
        <table style={variant === 'sheet' ? { '--excel-header-height': `${headerHeight}px`, width: `${sheetTableWidth}px` } as CSSProperties : undefined} className={`w-full min-w-full border-collapse text-left text-xs font-sans ${variant === 'sheet' ? 'excel-sheet-table' : ''}`}>
          {variant === 'sheet' && <colgroup>{visibleColumns.map((column) => <col key={column.key} style={{ width: resolveColumnWidth(column) ?? column.minWidth ?? 100 }} />)}{showActions && <col style={{ width: 144 }} />}</colgroup>}
          <thead>
            <tr className="border-b border-slate-300 bg-slate-100/90 text-slate-700 font-semibold select-none">
              {visibleColumns.map((col, idx) => {
                const style: CSSProperties = { position: 'sticky', top: 0, zIndex: fill ? 8 : 4, ...(fill ? { backgroundColor: '#f1f5f9' } : {}) }
                if (resolveColumnWidth(col)) style.width = resolveColumnWidth(col)
                if (col.minWidth) style.minWidth = col.minWidth
                if (col.stickyLeft !== undefined) {
                  style.left = stickyOffsets[col.key]?.left ?? 0
                  style.zIndex = fill ? 9 : 7
                }
                if (col.stickyRight !== undefined) { style.right = stickyOffsets[col.key]?.right ?? 0; style.zIndex = fill ? 9 : 7 }
                const isStt = col.key === 'id' || col.label === 'STT'
                const isSortable = col.sortable !== false && !isStt
                const isFilterable = col.filterable !== false && !isStt

                const sortIndex = sortConfig.findIndex((s) => s.key === col.key)
                const isSorted = sortIndex !== -1
                const sortDir = isSorted ? sortConfig[sortIndex].direction : null

                const isFilterActiveForCol = isColumnFilterActive(col.key, filterState, data)

                return (
                  <th
                    key={col.key || idx}
                    style={style}
                    data-frozen={col.stickyLeft !== undefined ? 'left' : col.stickyRight !== undefined ? 'right' : undefined}
                    title={col.headerTooltip || col.label}
                    className={`relative px-2.5 py-2 border-r border-slate-200 last:border-r-0 ${
                      col.stickyLeft !== undefined || col.stickyRight !== undefined ? 'bg-slate-100' : ''
                    } ${
                      col.align === 'right'
                        ? 'text-right'
                        : col.align === 'center'
                        ? 'text-center'
                        : 'text-left'
                    } ${col.stickyLeft !== undefined || col.stickyRight !== undefined ? 'excel-sheet-pinned' : ''} ${col.key === lastPinnedColumnKey ? 'excel-sheet-pinned-last' : ''} ${col.multiline ? 'excel-sheet-multiline' : ''}`}
                    onContextMenu={(event) => { if (!resizableColumns) return; event.preventDefault(); setColumnMenu({ key: col.key, x: event.clientX, y: event.clientY }) }}
                  >
                    <div
                      className={`flex items-center gap-1 ${
                        col.align === 'right'
                          ? 'justify-end'
                          : col.align === 'center'
                          ? 'justify-center'
                          : 'justify-between'
                      }`}
                    >
                      <div
                        onClick={
                          isSortable
                            ? (e) => handleSortClick(col.key, e.shiftKey)
                            : undefined
                        }
                        title={
                          isStt
                            ? 'Click STT để chọn dòng (Ctrl/Shift chọn nhiều)'
                          : isSortable
                            ? `${col.label}${col.headerTooltip ? ` — ${col.headerTooltip}` : ''} (Click sắp xếp, Shift+Click để thêm cột)`
                            : col.label
                        }
                        className={`inline-flex items-center gap-1 select-none min-w-0 ${
                          isSortable ? 'cursor-pointer hover:text-slate-900 group/th' : ''
                        }`}
                      >
                        <span className="excel-sheet-header-label">{col.label}</span>
                        {isSorted && (
                          <span className="inline-flex items-center gap-0.5 text-sky-600 font-bold shrink-0">
                            {sortDir === 'asc' ? (
                              <ArrowUp className="h-3 w-3" />
                            ) : (
                              <ArrowDown className="h-3 w-3" />
                            )}
                            {sortConfig.length > 1 && (
                              <span className="inline-flex items-center justify-center rounded-full bg-sky-100 text-sky-700 text-[10px] w-3.5 h-3.5 leading-none">
                                {sortIndex + 1}
                              </span>
                            )}
                          </span>
                        )}
                      </div>

                      {isFilterable && (
                        <button
                          type="button"
                          onClick={(e) => handleOpenFilter(col.key, e)}
                          aria-label={`Lọc ${col.label}`}
                          className={`p-1 rounded transition-colors cursor-pointer shrink-0 ${
                            isFilterActiveForCol
                              ? 'text-sky-600 bg-sky-100 hover:bg-sky-200 ring-1 ring-sky-300'
                              : 'text-slate-400 hover:text-slate-700 hover:bg-slate-200/60'
                          }`}
                          title={
                            isFilterActiveForCol
                              ? 'Cột đang lọc (click để chỉnh)'
                              : `Lọc cột ${col.label}`
                          }
                        >
                          <Filter className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                    {resizableColumns && <span aria-label={`Đổi độ rộng ${col.label}`} title="Kéo để đổi độ rộng · nhấp đúp để tự vừa" className="excel-column-resizer" onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); setResizingColumn({ key: col.key, startX: event.clientX, startWidth: resolveColumnWidth(col) ?? event.currentTarget.parentElement?.getBoundingClientRect().width ?? 100 }) }} onDoubleClick={(event) => { event.preventDefault(); event.stopPropagation(); const th = event.currentTarget.parentElement; const headerText = th?.querySelector<HTMLElement>('.inline-flex')?.scrollWidth ?? 0; const cells = Array.from(containerRef.current?.querySelectorAll<HTMLElement>(`[data-column-key="${CSS.escape(col.key)}"]`) ?? []); const contentWidth = Math.max(headerText, ...cells.map((cell) => Math.max(cell.firstElementChild?.scrollWidth ?? 0, cell.scrollWidth))); persistColumnWidths({ ...columnWidths, [col.key]: autoFitExcelColumnWidth(contentWidth + 16) }) }} />}
                  </th>
                )
              })}
              {showActions && (!readOnly || variant === 'sheet') && (
                <th
                  style={variant === 'sheet' ? { position: 'sticky', right: stickyOffsets[actionColumnKey]?.right ?? 0, zIndex: fill ? 9 : 7, width: 144, background: '#efefef' } : undefined}
                  data-frozen={variant === 'sheet' ? 'right' : undefined}
                  className="px-2 py-2.5 text-center text-slate-500 font-medium w-10 min-w-10"
                  aria-label="Thao tác"
                >
                  •••
                </th>
              )}
            </tr>
          </thead>
          {summaryRow && <tbody className="excel-sheet-summary-body">{typeof summaryRow === 'function' ? summaryRow(visibleRows.map((entry) => entry.row), stickyOffsets, visibleColumns) : summaryRow}</tbody>}
          <tbody className="divide-y divide-slate-200">
            {visibleRows.length === 0 ? (
              <tr>
                <td
                  colSpan={visibleColumns.length + (showActions && (!readOnly || variant === 'sheet') ? 1 : 0)}
                  className="px-4 py-8 text-left text-slate-500"
                >
                  <div className="sticky left-0 w-fit max-w-[min(100%,48rem)] pl-2">
                  <p className="text-xs mb-3">
                    {isFiltering
                      ? 'Không có mặt hàng nào khớp với bộ lọc hiện tại. Bấm "Xóa tất cả bộ lọc" để xem lại toàn bộ.'
                      : emptyMessage}
                  </p>
                  {isFiltering ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={handleClearAllFilters}
                      className="inline-flex items-center gap-1.5"
                    >
                      Xóa tất cả bộ lọc
                    </Button>
                  ) : (
                    !readOnly && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={handleAddRow}
                        className="inline-flex items-center gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {addRowLabel}
                      </Button>
                    )
                  )}
                  </div>
                </td>
              </tr>
            ) : (
              <>
              {topSpacerHeight > 0 && <tr aria-hidden="true" data-virtual-spacer="top"><td colSpan={visibleColumns.length + (showActions && (!readOnly || variant === 'sheet') ? 1 : 0)} style={{ height: topSpacerHeight, padding: 0, border: 0 }} /></tr>}
              {renderedRowIndexes.map((visibleRowIndex) => {
                const { originalIndex, row } = visibleRows[visibleRowIndex]!
                const key = rowKey ? rowKey(row, originalIndex) : (row.id as string) || originalIndex
                const rowSelected = selectedRows.has(visibleRowIndex)

                return (
                  <tr
                    key={key}
                    style={fill && !visibleColumns.some((column) => column.multiline) ? { height: rowHeight } : undefined}
                    className={`transition-colors group ${dropRowIndex === visibleRowIndex ? 'border-t-2 border-t-blue-600' : ''} ${
                      rowSelected
                        ? 'bg-amber-50/90'
                        : `${getRowClassName?.(row) ?? ''} hover:bg-sky-50/30`
                    }`}
                    onDragOver={(event) => {
                      if (!onRowReorder || draggedRowIndex === null || sortConfig.length || Object.keys(filterState).length) return
                      event.preventDefault()
                      setDropRowIndex(visibleRowIndex)
                    }}
                    onDrop={(event) => {
                      event.preventDefault()
                      if (onRowReorder && draggedRowIndex !== null && draggedRowIndex !== visibleRowIndex && !sortConfig.length && !Object.keys(filterState).length) {
                        const source = visibleRows[draggedRowIndex]?.row
                        if (source) onRowReorder(source, row)
                      }
                      setDraggedRowIndex(null)
                      setDropRowIndex(null)
                    }}
                    onDragEnd={() => { setDraggedRowIndex(null); setDropRowIndex(null) }}
                  >
                    {visibleColumns.map((col, visibleColIndex) => {
                      const colIndex = visibleColumnIndices[visibleColIndex]!
                      const selectionColIndex = visibleColumnIndexMap.get(colIndex) ?? visibleColIndex
                      const isSttCol = col.key === 'id' || col.label === 'STT'
                      const cellSelected =
                        !rowSelected &&
                        hasCellSelection &&
                        selAnchor &&
                        selFocus &&
                        isCellInRange(visibleRowIndex, selectionColIndex, selAnchor, selFocus)

                      return (
                        <td
                          key={col.key}
                          data-excel-row={visibleRowIndex}
                          data-excel-col={selectionColIndex}
                          style={col.stickyLeft !== undefined ? { position: 'sticky', left: stickyOffsets[col.key]?.left ?? 0, zIndex: 2 } : col.stickyRight !== undefined ? { position: 'sticky', right: stickyOffsets[col.key]?.right ?? 0, zIndex: 3 } : undefined}
                          data-frozen={col.stickyLeft !== undefined ? 'left' : col.stickyRight !== undefined ? 'right' : undefined}
                          data-column-key={col.key}
                          className={`p-0 border-r border-slate-200 last:border-r-0 align-middle ${col.multiline ? 'excel-sheet-multiline' : ''} ${col.stickyLeft !== undefined || col.stickyRight !== undefined ? 'excel-sheet-pinned' : ''} ${col.key === lastPinnedColumnKey ? 'excel-sheet-pinned-last' : ''} ${
                            isSttCol ? 'cursor-pointer bg-inherit' : col.stickyLeft !== undefined || col.stickyRight !== undefined ? 'bg-inherit' : ''
                          }`}
                          onClick={
                            isSttCol
                              ? (e) => handleRowHeaderClick(e, visibleRowIndex)
                              : undefined
                          }
                          onContextMenu={isSttCol && onRowContextMenu ? (event) => { event.preventDefault(); onRowContextMenu(event, row, visibleRowIndex, !sortConfig.length && !Object.keys(filterState).length) } : undefined}
                          draggable={Boolean(onRowReorder) && isSttCol && !sortConfig.length && !Object.keys(filterState).length}
                          onDragStart={isSttCol && onRowReorder ? (event) => { setDraggedRowIndex(visibleRowIndex); event.dataTransfer.effectAllowed = 'move' } : undefined}
                        >
                          <EditableCell
                            row={row}
                            rowIndex={visibleRowIndex}
                            column={col}
                            colIndex={colIndex}
                            onCommit={handleCellCommit}
                            onCellFocus={handleCellFocus}
                            onPasteTsv={handlePasteTsv}
                            tableReadOnly={readOnly}
                            isSelected={Boolean(cellSelected) || rowSelected}
                            onCellMouseDown={
                              isSttCol ? undefined : (e, row, _col) => handleCellMouseDown(e, row, selectionColIndex)
                            }
                            onCellMouseEnter={
                              isSttCol ? undefined : (row, _col) => handleCellMouseEnter(row, selectionColIndex)
                            }
                            selectionColIndex={selectionColIndex}
                            onCellNavigate={navigateGridCell}
                            onPasteDenied={onPasteDenied}
                          />
                        </td>
                      )
                    })}

                    {showActions && (!readOnly || variant === 'sheet') && (
                      <td style={variant === 'sheet' ? { position: 'sticky', right: stickyOffsets[actionColumnKey]?.right ?? 0, zIndex: 3 } : undefined} className={`px-1 py-1 text-center align-middle ${variant === 'sheet' ? 'w-[144px] min-w-[144px] bg-[#f7f7f7]' : 'w-10'}`}>
                        <div className={variant === 'sheet' ? 'inline-flex items-center justify-center gap-0.5 whitespace-nowrap' : undefined}>
                        {variant === 'sheet' && onRowQuickAction && <>
                          {(['move-up', 'move-down', 'delete', 'toggle-dim'] as const).map((action) => {
                            const reorderAvailable = !sortConfig.length && !Object.keys(filterState).length
                            const disabledReason = getRowQuickActionDisabledReason?.(action, row)
                              ?? ((action === 'move-up' || action === 'move-down') && !reorderAvailable ? 'Bỏ sắp xếp/lọc để đổi thứ tự.' : null)
                            const label = action === 'move-up' ? 'Di chuyển lên' : action === 'move-down' ? 'Di chuyển xuống' : action === 'delete' ? 'Xóa dòng' : 'Đổi đơn vị dòng'
                            return <button key={action} type="button" aria-label={label} title={disabledReason ?? label} disabled={Boolean(disabledReason)} onClick={() => onRowQuickAction(action, row, visibleRowIndex, !sortConfig.length && !Object.keys(filterState).length)} className={`p-1 ${action === 'delete' ? 'text-red-700 hover:bg-red-50' : 'text-slate-600 hover:bg-slate-200'} disabled:cursor-not-allowed disabled:opacity-35`}>
                              {action === 'move-up' ? <ArrowUp className="h-3.5 w-3.5" /> : action === 'move-down' ? <ArrowDown className="h-3.5 w-3.5" /> : action === 'delete' ? <Trash2 className="h-3.5 w-3.5" /> : <span className="text-[10px]">{row.dim_unit === 'mm' ? 'inch' : 'mm'}</span>}
                            </button>
                          })}
                        </>}
                        <RowActionMenu
                          row={row}
                          rowIndex={visibleRowIndex}
                          onOpenMenu={onRowActionMenu as ((event: ReactMouseEvent<HTMLElement>, row: Record<string, unknown>, rowIndex: number, canReorder: boolean) => void) | undefined}
                          onInsertBelow={() => handleInsertBelow(visibleRowIndex)}
                          onDelete={() => handleDeleteRow(visibleRowIndex)}
                          onMoveUp={() => handleMoveUp(visibleRowIndex)}
                          onMoveDown={() => handleMoveDown(visibleRowIndex)}
                          onDuplicate={() => handleDuplicate(visibleRowIndex)}
                          canMoveUp={sortConfig.length === 0 && visibleRowIndex > 0}
                          canMoveDown={sortConfig.length === 0 && visibleRowIndex < visibleRows.length - 1}
                          isSorted={sortConfig.length > 0}
                        />
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })
              }
              {bottomSpacerHeight > 0 && <tr aria-hidden="true" data-virtual-spacer="bottom"><td colSpan={visibleColumns.length + (showActions && (!readOnly || variant === 'sheet') ? 1 : 0)} style={{ height: bottomSpacerHeight, padding: 0, border: 0 }} /></tr>}
              </>
            )}
          </tbody>
        </table>
      </div>

      {fill && <style>{`
        .excel-table-fill-scroll{flex:1 1 0%;min-height:0;width:100%;overflow:auto}
        .excel-table-fill-scroll.excel-sheet-scroll{height:100%;min-height:0}
        .excel-editable-table-container[data-fill="true"] tbody:not(.excel-sheet-summary-body)>tr:not([data-virtual-spacer])>td{height:var(--excel-fill-row-height,34px);max-height:var(--excel-fill-row-height,34px);overflow:hidden}
        .excel-editable-table-container[data-fill="true"][data-density="compact"] tbody:not(.excel-sheet-summary-body)>tr:not([data-virtual-spacer])>td{padding-top:0;padding-bottom:0}
      `}</style>}

      {variant === 'sheet' && <style>{`
        .excel-sheet-variant{border-radius:2px;box-shadow:none;border-color:#999}
        .excel-sheet-scroll{width:100%;min-width:0;height:calc(100vh - 330px);min-height:320px;overflow:auto}
        .excel-sheet-table{font-family:Inter,Arial,sans-serif;font-size:12.5px;table-layout:fixed;width:max-content;min-width:100%}
        .excel-sheet-table th{background:#efefef;color:#222;border:1px solid #c4c4c4;padding:4px 4px 2px;font-size:11.5px;text-align:center;white-space:normal;line-height:1.25;vertical-align:middle}
        .excel-sheet-table th[data-frozen="left"],.excel-sheet-table th[data-frozen="right"]{z-index:7;background:#efefef}
        .excel-sheet-header-label{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden;white-space:normal;overflow-wrap:anywhere;line-height:1.2}
        .excel-sheet-table th>div{min-width:0;align-items:flex-start}
        .excel-sheet-table td{border:1px solid #c4c4c4;min-height:36px;padding:0;white-space:normal}
        .excel-sheet-table td>div,.excel-sheet-table td>textarea,.excel-sheet-table td>input{font-size:12.5px;padding:3px 5px;min-height:36px;line-height:1.3}
        .excel-sheet-table th.excel-sheet-pinned,.excel-sheet-table td[style*="position: sticky"]{background:#f5f5f5}
        .excel-sheet-table .excel-sheet-pinned-last{border-right:2px solid #888}
        .excel-sheet-table td.excel-sheet-multiline>div,.excel-sheet-table td.excel-sheet-multiline>textarea{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;white-space:normal;overflow:hidden;min-height:54px}
        .excel-sheet-summary-body{position:sticky;top:var(--excel-header-height, 30px);z-index:8}
        .excel-sheet-summary td[data-frozen]{background:#e5e7eb}
        .excel-sheet-summary td{background:#e2e2e2;border-bottom:2px solid #777;font-size:12.5px;font-weight:700;padding:5px}
        .excel-sheet-table .excel-sheet-summary td{background:#e2e2e2}
        .excel-sheet-table tr.bg-orange-50>td{background:#fdebd6}
        .excel-sheet-table tr.bg-emerald-50>td{background:#e3f3e3}
        .excel-sheet-table tr.bg-blue-50>td{background:#e3ebf6}
        .excel-sheet-table [class*="bg-sky-100/80"]{background-color:rgba(31,111,235,.16)!important}
        .excel-column-resizer{position:absolute;right:0;top:0;height:100%;width:6px;cursor:col-resize;z-index:10}
        .excel-column-resizer:hover{background:#1f6feb}
        .excel-resize-guide{position:fixed;top:0;bottom:0;width:2px;background:#1f6feb;z-index:110;pointer-events:none}
      `}</style>}
      {resizeGuideX !== null && <div className="excel-resize-guide" style={{ left: resizeGuideX }} />}

      {columnMenu && <div className="fixed inset-0 z-[100]" onMouseDown={() => setColumnMenu(null)}><div role="menu" className="fixed rounded border border-slate-200 bg-white p-1 shadow-lg" style={{ left: Math.min(columnMenu.x, window.innerWidth - 200), top: Math.min(columnMenu.y, window.innerHeight - 64) }} onMouseDown={(event) => event.stopPropagation()}><button role="menuitem" className="px-3 py-2 text-left text-sm hover:bg-slate-100" onClick={() => resetColumnWidth(columnMenu.key)}>↔ Đặt lại độ rộng cột</button></div></div>}

      {activeFilterCol && filterAnchorRect && (
        <ExcelFilterPopover
          column={visibleColumns.find((c) => c.key === activeFilterCol)!}
          data={data}
          filterItem={filterState[activeFilterCol]}
          anchorRect={filterAnchorRect}
          onApply={(item) => {
            setFilterState((prev) => {
              const next = { ...prev }
              if (!item) {
                delete next[activeFilterCol]
              } else {
                next[activeFilterCol] = item
              }
              return next
            })
            setActiveFilterCol(null)
            setFilterAnchorRect(null)
          }}
          onClose={() => {
            setActiveFilterCol(null)
            setFilterAnchorRect(null)
          }}
        />
      )}

      {data.length > 0 && (
        <div className={`excel-table-selection-footer flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/60 px-3 ${fill ? 'py-1' : 'py-2'}`}>
          {!readOnly && showAddRowButton && <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleAddRow}
            className="text-xs text-sky-700 hover:text-sky-800 hover:bg-sky-50 cursor-pointer flex items-center gap-1 font-medium"
          >
            <Plus className="h-3.5 w-3.5" />
            {addRowLabel}
          </Button>}

          <span className="text-[11px] text-slate-400">
            Chọn vùng ô (kéo / Shift) · Click STT chọn dòng · Delete xóa ·
            Ctrl+V dán Excel
          </span>
        </div>
      )}
    </div>
  )
}
